import React, { useEffect, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import {
  callApi,
  callApiWithOptions,
  HttpMethod,
} from "src/services/apiService";
import Loading from "src/components/Loading";
import DisplayRecord from "./DisplayRecord";
import InfoModal from "src/components/InfoModal";
import ConfirmationModal from "src/components/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import IDisplayRecords from "src/interfaces/LogoCall/IDisplayRecords";
import PhoneNumbers from "./PhoneNumbers";
import { formatPhoneNumber } from "src/utils/formatUtils";

// Constants
const ENTERPRISE_REQUIRED_MESSAGE =
  "You must create an Enterprise before you can create Display Records. Please go to the Enterprise page to create one.";

const DisplayRecords: React.FC = () => {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<IDisplayRecords[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<IDisplayRecords>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enterpriseId, setEnterpriseId] = useState<string>("");
  const [hasEnterprise, setHasEnterprise] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [isPhoneNumbersOpen, setIsPhoneNumbersOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<IDisplayRecords | null>(
    null,
  );

  const navigate = useNavigate();

  // Handlers
  const handleGoToEnterprise = () => navigate("/logocall/enterprise");

  const handleAddClick = () => {
    if (!hasEnterprise) {
      setError(ENTERPRISE_REQUIRED_MESSAGE);
      return;
    }
    setSelectedRecord(undefined);
    setIsModalOpen(true);
  };

  const handleEditDIRClick = (record: IDisplayRecords) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const handleDeleteDIRClick = (record: IDisplayRecords) => {
    setDeleteRecord(record);
  };

  const handleConfirmDelete = () => {
    if (!deleteRecord) return;
    setDeleteRecord(null);
    callApiWithOptions({
      endpoint: `bcid/dir/${deleteRecord.displayIdentityRecordId}`,
      method: HttpMethod.DELETE,
    })
      .then(() => {
        loadData();
      })
      .catch((err) => {
        console.error(err);
        setError("An error occurred while deleting the record.");
      });
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if enterprise exists
      const response = await callApiWithOptions({
        endpoint: "bcid/enterprise",
        method: HttpMethod.GET,
        allowedErrorStatus: [404],
      });

      if (response.status === 404) {
        setHasEnterprise(false);
        setShowEnterpriseModal(true);
        return;
      }

      setHasEnterprise(true);

      // Load display records
      const data = await callApi("bcid/dir", HttpMethod.GET);
      setRecords(data);
      setEnterpriseId(data[0]?.enterpriseId || "");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "An error occurred while loading display records";
      setError(message);
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneNumbersClick = (record: IDisplayRecords) => {
    setSelectedRecord(record);
    setSelectedRecordId(record.displayIdentityRecordId);
    setIsPhoneNumbersOpen(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      Display Records allow you to manage your branded calling information. Each
      record contains:
      <ul>
        <li>A Calling Name that will be shown to call recipients</li>
        <li>A Calling Logo that will be displayed during incoming calls</li>
        <li>Phone Numbers that are branded with this display information</li>
      </ul>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <button className="btn btn-primary mb-3" onClick={handleAddClick}>
        Add Display Record
      </button>
      {isLoading ? (
        <Loading />
      ) : (
        <Grid
          data={records}
          reorderable={true}
        >
          <GridColumn field="rcdName" title="Calling Name" width="200px" />
          <GridColumn
            title="Branded Numbers"
            width="200px"
            cell={(props) => {
              const phoneNumbers: string[] =
                props.dataItem?.telephoneNumberSample || [];

              return (
                <td>
                  <div style={{ fontSize: "0.875rem" }}>
                    {phoneNumbers.length > 0 ? (
                      phoneNumbers.map((number, index) => (
                        <span key={index}>
                          {formatPhoneNumber(number)}
                          <br />
                        </span>
                      ))
                    ) : (
                      <span>No Numbers Available</span>
                    )}
                  </div>
                </td>
              );
            }}
          />

          <GridColumn
            field="logoUrl"
            title="Calling Logo"
            width="150px"
            cell={(props) => (
              <td className="text-center align-middle">
                {props.dataItem.logoUrl && (
                  <img
                    src={props.dataItem.logoUrl}
                    alt="Calling Logo"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </td>
            )}
          />
          <GridColumn
            field="status"
            title="Vetting Status"
            width="150px"
            cell={(props) => {
              const status = props.dataItem.status;
              const enterpriseStatus = props.dataItem.enterpriseStatus;
              let badgeClass = "text-bg-danger";
              let label = "Not Approved";
              let statusDetail: string | null = null;

              // If enterprise is not verified and DIR is approved, show as not approved
              // If enterprise is pending, DIR should also show as pending
              if (enterpriseStatus !== 1 && status === 1) {
                badgeClass = "text-bg-danger";
                label = "Not Approved";
                statusDetail = "Enterprise not approved";
              } else if (enterpriseStatus === 0 && status === 0) {
                badgeClass = "text-bg-info";
                label = "Pending";
                statusDetail = "Enterprise pending verification";
              } else if (enterpriseStatus !== 1 && status === 0) {
                badgeClass = "text-bg-danger";
                label = "Not Approved";
                statusDetail = "Enterprise not approved";
              } else if (status === -4) {
                badgeClass = "text-bg-dark";
                label = "Terminated";
              } else if (status === -3) {
                badgeClass = "text-bg-warning";
                label = "Paused";
              } else if (status === -2) {
                badgeClass = "text-bg-secondary";
                label = "Draft";
              } else if (status === -1) {
                badgeClass = "text-bg-primary";
                label = "Not Submitted";
              } else if (status === 0) {
                badgeClass = "text-bg-primary";
                label = "Pending";
              } else if (status === 1) {
                badgeClass = "text-bg-success";
                label = "Approved";
              } else {
                // status > 1 means rejected by vetting agent
                statusDetail = props.dataItem.statusName;
              }
              return (
                <td className="text-center align-middle">
                  <span
                    className={`badge ${badgeClass} rounded-pill p-2`}
                    style={{
                      fontWeight: "normal",
                      fontSize: "0.795rem",
                      lineHeight: "1",
                    }}
                  >
                    {label}
                  </span>
                  {statusDetail && (
                    <div className="text-danger small mt-1">{statusDetail}</div>
                  )}
                </td>
              );
            }}
          />
          <GridColumn
            title="Total Numbers"
            width="135px"
            cell={(props) => (
              <td className="text-center align-middle">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePhoneNumbersClick(props.dataItem);
                  }}
                >
                  <span className="badge rounded-pill text-bg-primary me-2">
                    {props.dataItem?.phoneNumberCount || 0}
                  </span>
                  Edit
                </button>
              </td>
            )}
          />

          <GridColumn
            title=""
            cell={(props) => (
              <td className="text-center align-middle">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleEditDIRClick(props.dataItem)}
                >
                  Edit
                </button>
                <button
                  className="ms-1 btn btn-sm btn-danger"
                  onClick={() => handleDeleteDIRClick(props.dataItem)}
                >
                  Delete
                </button>
              </td>
            )}
          />
        </Grid>
      )}
      <DisplayRecord
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          loadData();
        }}
        displayRecordId={selectedRecord?.displayIdentityRecordId ?? null}
        canWithdraw={selectedRecord?.canWithdraw}
        canRevert={selectedRecord?.canRevert}
        canEdit={selectedRecord?.canEdit}
      />
      <PhoneNumbers
        isOpen={isPhoneNumbersOpen}
        onClose={() => {
          setIsPhoneNumbersOpen(false);
          loadData();
        }}
        displayRecordId={selectedRecordId}
        enterpriseId={enterpriseId}
        callingName={selectedRecord?.rcdName ?? ""}
      />
      <InfoModal
        isOpen={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
        title="Enterprise Required"
        message={ENTERPRISE_REQUIRED_MESSAGE}
        actionLabel="Go to Enterprise"
        onAction={handleGoToEnterprise}
        required={true}
      />
      <ConfirmationModal
        isVisible={!!deleteRecord}
        title="Delete Record"
        message="Are you sure you want to delete this record?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteRecord(null)}
      />
    </div>
  );
};

export default DisplayRecords;
